import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';

export function useHls(
  videoRef: React.RefObject<HTMLVideoElement>,
  url: string | null,
  enabled: boolean = true,
) {
  const hlsRef = useRef<Hls>();
  const [levels, setLevels] = useState<{ height: number; idx: number }[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<number>(-1);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    const videoEl = videoRef.current!;
    // tear down old instance
    hlsRef.current?.destroy();
    setLevels([]);
    setSelectedLevel(-1);
    if (!enabled || !videoRef.current || !url) return;

    if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = url;
    } else if (!Hls.isSupported()) {
      return;
    }

    // always use Hls.js (even on Safari) so we can get `levels`
    const hls = new Hls({ maxBufferLength: 30,skipBufferDuringSeek: true });
    hlsRef.current = hls;
    hls.attachMedia(videoEl);

    // once master manifest is loaded, read available levels
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      setLevels(hls.levels.map((lvl, idx) => ({ height: lvl.height, idx })));
    });

    hls.loadSource(url);
    return () => {
      hls.destroy();
    };
  }, [url, videoRef]);

  const changeLevel = (level: number) => {
    const hls = hlsRef.current!;
    const video = videoRef.current!;
    const wasPlaying = !video.paused && !video.ended;

    setSwitching(true);
    setSelectedLevel(level);
    video.pause();

    hls.currentLevel = level; // lock into that rendition
    hls.trigger(Hls.Events.BUFFER_FLUSHING, {
      startOffset: 0,
      endOffset: Infinity,
      type: undefined,
    });
    hls.nextLevelSwitch?.();

    // wait for first fragment of new level
    const onFragment = () => {
      hls.off(Hls.Events.FRAG_BUFFERED, onFragment);
      // tiny seek to stay inside new buffered range
      const isStart = video.currentTime < 0.15;
      video.currentTime = isStart ? 0 : Math.max(video.currentTime - 0.05, 0);
      if (wasPlaying) video.play();
      setSwitching(false);
    };
    hls.on(Hls.Events.FRAG_BUFFERED, onFragment);
  };
  let hls: Hls = new Hls({ skipBufferDuringSeek: true });

  return {
    hls,
    changeLevel,
    levels,
    selectedLevel,
    switching,
  };
}
